import { AssembleeHemicycle } from "@logora/parliament.parliament_chamber.assemble_hemicycle";
import { ChamberLabel } from "@logora/parliament.parliament_chamber.chamber_label";
import { SenatHemicycle } from "@logora/parliament.parliament_chamber.senat_hemicycle";
import { MemberBox } from "@logora/parliament.parliament_member.member_box";
import { Box } from "@logora/parliament.ui.box";
import { Label } from "@logora/parliament.ui.label";
import dayjs from "dayjs";
import React, { useEffect, useState, useCallback } from "react";
import { useDataProvider } from "react-admin";
import { Link } from "react-router-dom";
import Tabs from "../components/Tabs";
import { Show } from "../components/show/Show";
import {
	participantDebatDataset,
	participantReunionDataset,
} from "../datasets";
import { formatEtat } from "../datasets/reunions";
import { DebatDetails } from "../debats";
import { capitalizeFirstLetter } from "../formatters";
import { MultiList } from "../search/MultiList";
import { FollowReunionButton } from "./FollowReunionButton";
import styles from "./ReunionShow.module.scss";

const ReunionDetails = ({ resource }) => {
	const dataProvider = useDataProvider();
	const [participants, setParticipants] = useState([]);
	const [orateurs, setOrateurs] = useState([]);
	const today = dayjs();

	useEffect(() => {
		if (resource?.nombreParticipants > 0) {
			fetchParticipants();
		}
		if (resource.compteRenduRef) {
			fetchOrateurs();
		}
	}, [resource]);

	const fetchOrateurs = useCallback(async () => {
		try {
			const response = await dataProvider.getList("interventions", {
				filter: {
					debatRefUid: resource.compteRenduRefUid,
					codeGrammaire: "PAROLE_GENERIQUE",
				},
				include: ["acteurRef.mandatPrincipal", "acteurRef.groupeParlementaire"],
				distinct: "acteurRefUid",
				pagination: { perPage: 1000 },
			});
			const orateurs = response.data;
			if (orateurs) {
				setOrateurs(orateurs);
			}
		} catch (error) {
			console.log(error);
		}
	});

	const fetchParticipants = useCallback(async () => {
		try {
			const response = await dataProvider.getList("participants_reunions", {
				filter: { agendaRefUid: resource.uid },
				include: ["acteurRef.mandatPrincipal", "acteurRef.groupeParlementaire"],
				pagination: { perPage: 1000 },
			});
			const participants = response.data;
			if (participants) {
				setParticipants(participants);
			}
		} catch (error) {
			console.log(error);
		}
	}, [dataProvider]);

	const getHighlightedSeats = () => {
		let membres = [];
		if (orateurs?.length) {
			membres = orateurs;
		} else if (participants?.length) {
			membres = participants;
		}
		return membres.map((m) => {
			return {
				id: Number.parseInt(m?.acteurRef?.placeHemicycle || m?.placeHemicycle),
				color: m?.acteurRef?.groupeParlementaire?.couleurAssociee || "#777",
				labelBackgroundColor: "#FFF",
				label: (
					<Link
						className={styles.labelHemicycle}
						to={`/parliament/acteurs/${m?.acteurRefUid}`}
						target="_blank"
					>
						<MemberBox
							firstName={m?.acteurRef?.prenom}
							lastName={m?.acteurRef?.nom}
							imageUrl={m?.acteurRef?.urlImage}
							parliamentGroup={{
								color: m?.acteurRef?.groupeParlementaire?.couleurAssociee,
								groupName: m?.acteurRef?.groupeParlementaire?.libelle,
							}}
						/>
					</Link>
				),
				position: "top",
			};
		});
	};

	const getTabData = () => {
		const tabData = [];
		if (resource.compteRenduRef) {
			tabData.push({
				label: "Compte-rendu",
				content: (
					<div role="tabpanel" value={0} index={0}>
						<DebatDetails resource={resource.compteRenduRef} />
					</div>
				),
			});
		}
		if (resource.nombreParticipants > 0) {
			tabData.push({
				label: "Participants",
				content: (
					<div role="tabpanel" value={1} index={1}>
						<MultiList
							datasets={
								resource.compteRenduRef
									? {
											participants_debats: participantDebatDataset,
										}
									: {
											participants_reunions: participantReunionDataset,
										}
							}
							globalHiddenFilters={
								resource.compteRenduRef
									? {
											debatRefUid: resource.compteRenduRefUid,
											codeGrammaire: "PAROLE_GENERIQUE",
										}
									: { agendaRefUid: resource.uid }
							}
							showListSearch={true}
						/>
					</div>
				),
			});
		}
		return tabData;
	};

	return (
		<>
			<div className={styles.header}>
				<ChamberLabel chamber={resource.chambre} />
				<Label text={"Réunion"} />
				{dayjs(resource.timestampDebut).isAfter(today) && (
					<FollowReunionButton sx={{ marginLeft: "auto" }} reunion={resource} />
				)}
			</div>
			<div className={styles.title}>
				{capitalizeFirstLetter(
					resource.objet || resource.compteRenduRef?.quantiemesJournee,
				)}
			</div>
			<div className={styles.presentation}>
				<div className={styles.left}>
					{resource.organeReunionRef && (
						<div className={styles.box}>
							<Box title="Organe" className={styles.boxContainer}>
								<div>{resource.organeReunionRef.libelle}</div>
							</Box>
						</div>
					)}
					<div className={styles.box}>
						<Box title="Date" className={styles.boxContainer}>
							<div>
								{dayjs(resource.timestampDebut).format(
									"dddd D MMMM YYYY [à] HH[h]mm",
								)}
							</div>
						</Box>
					</div>
					<div className={styles.box}>
						<Box title="État de la réunion" className={styles.boxContainer}>
							{formatEtat(resource)}
						</Box>
					</div>
					<div className={styles.box}>
						<Box
							title={
								resource.compteRenduRef
									? "Nombre d'orateurs"
									: "Nombre de participants"
							}
							className={styles.boxContainer}
						>
							<div>
								{resource.compteRenduRef
									? `${orateurs.length} orateurs`
									: `${resource.nombreParticipants} participants`}
							</div>
						</Box>
					</div>
				</div>
				<div className={styles.box}>
					<Box
						title={resource.compteRenduRef ? "Orateurs" : "Participants"}
						className={styles.boxContainer}
					>
						{resource.chambre === "AN" && (
							<AssembleeHemicycle
								className={styles.svgIcon}
								highlightedSeats={getHighlightedSeats(
									resource.compteRenduRef && orateurs ? orateurs : participants,
								)}
							/>
						)}

						{resource.chambre === "SN" && (
							<SenatHemicycle
								className={styles.svgIcon}
								highlightedSeats={getHighlightedSeats(
									resource.compteRenduRef && orateurs ? orateurs : participants,
								)}
							/>
						)}
					</Box>
				</div>
			</div>
			<div className={styles.corps}>
				<div className={styles.tabContainer}>
					<div>
						<Tabs tabData={getTabData()} />
					</div>
				</div>
			</div>
		</>
	);
};

export const ReunionShow = () => {
	return (
		<Show
			resource={"reunions"}
			includes={["compteRenduRef", "organeReunionRef"]}
		>
			<ReunionDetails />
		</Show>
	);
};
